/**
 * ExternalPaymentsHubPage
 * Central hub for reviewing external payment detection results (Zelle, cash, check).
 * Account linking and setup wizard have moved to /settings/linked-accounts.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Settings } from 'lucide-react';
import {
  fetchExternalPaymentsHubSummary,
  confirmExternalPaymentMatch,
  dismissExternalPaymentTransaction,
  fetchIgnoreRules,
  ignoreTransaction,
  updateIgnoreRule,
  deleteIgnoreRule,
  type ExternalPaymentHubSummary,
  type IgnoreRule,
} from '../api/external-payments-hub.api';
import {
  fetchAllPendingLateFees,
  approvePendingLateFee,
  dismissPendingLateFee,
  type PendingLateFee,
} from '../lib/api';
import { PageHeader } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { HelpBlock, HelpTooltip } from '../components/ui/HelpTooltip';
import { PendingMatchesFeed } from '../components/external-payments/PendingMatchesFeed';
import { UnmatchedTransactionsFeed } from '../components/external-payments/UnmatchedTransactionsFeed';
import { IgnoreRulesSection } from '../components/external-payments/IgnoreRulesSection';
import { formatLocalDate } from '../utils/date';

export function ExternalPaymentsHubPage(): JSX.Element {
  const [hubData, setHubData] = useState<ExternalPaymentHubSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAutoAppliedExpanded, setIsAutoAppliedExpanded] = useState(false);
  const [pendingLateFees, setPendingLateFees] = useState<PendingLateFee[]>([]);
  const [isApprovingFee, setIsApprovingFee] = useState<string | null>(null);
  const [isDismissingFee, setIsDismissingFee] = useState<string | null>(null);
  const [lateFeeError, setLateFeeError] = useState<string | null>(null);
  const [ignoreRules, setIgnoreRules] = useState<IgnoreRule[]>([]);

  const loadHubData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [hubSummary, lateFeesData, rulesData] = await Promise.all([
        fetchExternalPaymentsHubSummary(),
        fetchAllPendingLateFees(),
        fetchIgnoreRules(),
      ]);
      setHubData(hubSummary);
      setPendingLateFees(lateFeesData.pendingLateFees.filter(f => f.status === 'pending'));
      setIgnoreRules(rulesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load external payments data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHubData();
  }, [loadHubData]);

  const handleConfirmMatch = async (_matchId: string, transactionId: string) => {
    await confirmExternalPaymentMatch(transactionId);
    await loadHubData();
  };

  const handleDismissMatch = async (transactionId: string) => {
    await dismissExternalPaymentTransaction(transactionId);
    await loadHubData();
  };

  const handleIgnoreTransaction = async (
    transactionId: string,
    rule: { field: string; operator: string; value: string }
  ) => {
    await ignoreTransaction(transactionId, rule);
    await loadHubData();
  };

  const handleDeleteIgnoreRule = async (ruleId: string) => {
    await deleteIgnoreRule(ruleId);
    setIgnoreRules((prev) => prev.filter((r) => r.id !== ruleId));
  };

  const handleEditIgnoreRule = async (
    ruleId: string,
    patch: Partial<Pick<IgnoreRule, 'field' | 'operator' | 'value'>>
  ) => {
    const updated = await updateIgnoreRule(ruleId, patch);
    setIgnoreRules((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  };

  const handleApprovePendingLateFee = async (id: string) => {
    setIsApprovingFee(id);
    setLateFeeError(null);
    try {
      await approvePendingLateFee(id);
      const result = await fetchAllPendingLateFees();
      setPendingLateFees(result.pendingLateFees.filter(f => f.status === 'pending'));
    } catch (err) {
      setLateFeeError(err instanceof Error ? err.message : 'Failed to approve late fee');
    } finally {
      setIsApprovingFee(null);
    }
  };

  const handleDismissPendingLateFee = async (id: string) => {
    setIsDismissingFee(id);
    setLateFeeError(null);
    try {
      await dismissPendingLateFee(id);
      const result = await fetchAllPendingLateFees();
      setPendingLateFees(result.pendingLateFees.filter(f => f.status === 'pending'));
    } catch (err) {
      setLateFeeError(err instanceof Error ? err.message : 'Failed to dismiss late fee');
    } finally {
      setIsDismissingFee(null);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
      Math.abs(amount)
    );


  if (isLoading) {
    return <HubPageSkeleton />;
  }

  return (
    <div className="max-w-4xl mx-auto pb-6" data-testid="external-payments-hub-page">
      {/* Header */}
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="External Payments"
          subtitle="Review and confirm matched Zelle, cash, and check payments"
          action={
            <Link to="/settings/linked-accounts" data-testid="manage-linked-accounts-link">
              <Button variant="outline" className="min-h-[44px] gap-2">
                <Settings className="h-4 w-4" aria-hidden="true" />
                Manage Linked Accounts
              </Button>
            </Link>
          }
        />
      </div>

      {/* Help block */}
      <div className="px-[var(--page-padding-x)] mb-4">
        <HelpBlock
          summary="This is your central hub for managing payments received outside the platform — Zelle, cash, checks, or direct bank transfers. The system scans your linked bank account and tries to match incoming deposits to your tenants."
          details={"How it works: When you link a bank account and enable external payment detection on a lease, the system monitors your account for deposits that look like rent payments. It matches them using patterns it learns from your tenants' payment history.\n\nSections on this page:\n\u2022 Connection Issues \u2014 Alerts if a linked bank account has been disconnected or has errors.\n\u2022 Pending Late Fees \u2014 Late fees that need your approval before being charged. Unlike ACH leases, external payment late fees are never automatic.\n\u2022 Pending Matches \u2014 Deposits the system thinks belong to a tenant but wants you to confirm. Confirming teaches the system to recognize this pattern automatically next time.\n\u2022 Unmatched Transactions \u2014 Deposits the system couldn't identify. You can manually match them to a lease or dismiss them.\n\u2022 Recently Auto-Applied \u2014 Payments the system matched and applied automatically (no action needed).\n\u2022 Ignore Rules \u2014 Filters you've created to permanently hide recurring non-rent transactions (e.g., internal transfers)."}
        />
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
          role="alert"
        >
          <p className="text-destructive m-0">{error}</p>
          <Button variant="outline" size="sm" onClick={loadHubData} className="mt-2">
            <RefreshCw className="h-3 w-3 mr-1" /> Retry
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-6 px-[var(--page-padding-x)]">
        {/* Connection Alerts */}
        {hubData && hubData.connectionAlerts.length > 0 && (
          <section data-testid="hub-section-alerts">
            <h2 className="text-sm font-semibold text-foreground mb-2">Connection Issues</h2>
            <div className="flex flex-col gap-2">
              {hubData.connectionAlerts.map((alert) => (
                <div
                  key={alert.linkedBankAccountId}
                  className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3"
                  role="alert"
                >
                  <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-destructive m-0">
                      {alert.institutionName} ···{alert.accountMask}
                    </p>
                    <p className="text-xs text-destructive/80 m-0">
                      {alert.status === 'disconnected'
                        ? 'Bank account disconnected — re-authentication required'
                        : 'Connection error — please reconnect your account'}
                    </p>
                    <Link
                      to="/settings/linked-accounts"
                      className="text-xs text-destructive font-medium underline mt-0.5 inline-block"
                    >
                      Go to Linked Accounts →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Pending Late Fees */}
        {pendingLateFees.length > 0 && (
          <section data-testid="hub-section-pending-late-fees">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-foreground m-0 inline-flex items-center gap-1.5">
                Pending Late Fees
                <span className="text-xs font-medium text-warning bg-warning/10 border border-warning/30 px-1.5 py-0.5 rounded">
                  {pendingLateFees.length}
                </span>
                <HelpTooltip text="For external payment leases, late fees require your approval because the system can't be 100% certain a payment hasn't been made. Approve to charge the fee to the tenant's ledger, or dismiss if you know payment is on the way." />
              </h2>
            </div>
            {lateFeeError && (
              <p className="text-sm text-destructive mb-2" role="alert">{lateFeeError}</p>
            )}
            <div className="flex flex-col gap-2">
              {pendingLateFees.map(fee => (
                <div
                  key={fee.id}
                  className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-3"
                  data-testid={`hub-pending-late-fee-${fee.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground m-0 truncate">{fee.description}</p>
                    <p className="text-xs text-muted-foreground m-0">
                      {formatCurrency(fee.amount)}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0 ml-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleApprovePendingLateFee(fee.id)}
                      disabled={isApprovingFee === fee.id || isDismissingFee === fee.id}
                      data-testid={`hub-approve-late-fee-${fee.id}`}
                    >
                      {isApprovingFee === fee.id ? 'Approving...' : 'Approve'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDismissPendingLateFee(fee.id)}
                      disabled={isApprovingFee === fee.id || isDismissingFee === fee.id}
                      data-testid={`hub-dismiss-late-fee-${fee.id}`}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {isDismissingFee === fee.id ? 'Dismissing...' : 'Dismiss'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Pending Matches */}
        <section data-testid="hub-section-pending-matches">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-foreground m-0 inline-flex items-center gap-1.5">
              Pending Matches
              {hubData && hubData.totalPendingMatches > 0 && (
                <span className="text-xs font-medium text-warning bg-warning/10 border border-warning/30 px-1.5 py-0.5 rounded">
                  {hubData.totalPendingMatches}
                </span>
              )}
              <HelpTooltip text="The system found deposits that likely belong to a tenant but wants your confirmation. Confirming a match records the payment on the tenant's ledger and teaches the system to recognize this pattern automatically in the future.\n\nConfidence levels:\n\u2022 High \u2014 Strong match based on a previously confirmed alias.\n\u2022 Medium \u2014 Matched by tenant name.\n\u2022 Low \u2014 Partial match; review carefully." />
            </h2>
          </div>
          <PendingMatchesFeed
            matches={hubData?.pendingMatches ?? []}
            totalCount={hubData?.totalPendingMatches ?? 0}
            onConfirm={handleConfirmMatch}
            onDismiss={handleDismissMatch}
          />
        </section>

        {/* Unmatched Transactions */}
        <section data-testid="hub-section-unmatched-transactions">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-foreground m-0 inline-flex items-center gap-1.5">
              Unmatched Transactions
              {hubData && hubData.totalUnmatchedTransactions > 0 && (
                <span className="text-xs font-medium text-destructive bg-destructive/10 border border-destructive/30 px-1.5 py-0.5 rounded">
                  {hubData.totalUnmatchedTransactions}
                </span>
              )}
              <HelpTooltip text="Deposits the system couldn't match to any tenant. You can manually match them to a lease, dismiss them one time, or create an ignore rule to permanently filter similar transactions (e.g., recurring transfers from another account that aren't rent)." />
            </h2>
          </div>
          <UnmatchedTransactionsFeed
            transactions={hubData?.unmatchedTransactions ?? []}
            totalCount={hubData?.totalUnmatchedTransactions ?? 0}
            onMatched={loadHubData}
            onDismiss={handleDismissMatch}
            onIgnore={handleIgnoreTransaction}
          />
        </section>

        {/* Recent Auto-Applied */}
        {hubData && hubData.recentAutoApplied.length > 0 && (
          <section data-testid="hub-section-auto-applied">
            <button
              type="button"
              className="flex items-center justify-between w-full text-left"
              onClick={() => setIsAutoAppliedExpanded((v) => !v)}
            >
              <h2 className="text-sm font-semibold text-foreground m-0 inline-flex items-center gap-1.5">
                Recently Auto-Applied
                <span className="text-xs font-medium text-success bg-success/10 border border-success/30 px-1.5 py-0.5 rounded">
                  {hubData.recentAutoApplied.length}
                </span>
                <HelpTooltip text="Payments the system matched and applied to the tenant's ledger automatically — no action needed from you. This happens once the system has learned a tenant's payment pattern through multiple confirmed matches and the lease is in Active mode." />
              </h2>
              <span className="text-xs text-muted-foreground">
                {isAutoAppliedExpanded ? 'Hide' : 'Show'}
              </span>
            </button>

            {isAutoAppliedExpanded && (
              <div className="mt-2 flex flex-col gap-2">
                {hubData.recentAutoApplied.map((payment) => (
                  <div
                    key={payment.transactionId}
                    className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {payment.tenantName}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {payment.propertyName} · {formatLocalDate(payment.date, { month: 'short', day: 'numeric' })}
                        {payment.aliasUsed && ` · via ${payment.aliasUsed}`}
                      </p>
                    </div>
                    <span className="text-sm font-medium text-success shrink-0 ml-3">
                      {formatCurrency(payment.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Ignore Rules */}
        {ignoreRules.length > 0 && (
          <section>
            <IgnoreRulesSection
              rules={ignoreRules}
              onDelete={handleDeleteIgnoreRule}
              onEdit={handleEditIgnoreRule}
              isLoading={isLoading}
            />
          </section>
        )}
      </div>
    </div>
  );
}

function HubPageSkeleton() {
  return (
    <div className="max-w-4xl mx-auto pb-6" data-testid="external-payments-hub-page">
      <div className="px-[var(--page-padding-x)]">
        <Skeleton className="h-8 w-64 mb-2" />
        <Skeleton className="h-4 w-96 mb-6" />
      </div>
      <div className="px-[var(--page-padding-x)] space-y-4">
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>
    </div>
  );
}

export default ExternalPaymentsHubPage;
